import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowUpRight } from 'lucide-react';

// ─── strip HTML utility ───────────────────────────────────────────────────────
const stripHtml = (html) => {
  if (!html) return '';
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim();
};

// ─── accent helpers ───────────────────────────────────────────────────────────
const accentOf = (cat) =>
  cat === 'east' ? '#dc2626' : cat === 'west' ? '#06b6d4' : '#a78bfa';

const glowOf = (cat) =>
  cat === 'east'
    ? 'rgba(220,38,38,0.55)'
    : cat === 'west'
    ? 'rgba(6,182,212,0.55)'
    : 'rgba(167,139,250,0.55)';

const labelOf = (cat) =>
  cat === 'east' ? 'ANIME & MANGA' : cat === 'west' ? 'MOVIES & CARTOONS' : 'ENTERTAINMENT HUB';

// ─── styles ──────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@700;900&display=swap');

  @font-face {
    font-family: 'Japanese3017';
    src: url('/fonts/Japanese-3017.ttf') format('truetype');
    font-display: swap;
  }
  @font-face {
    font-family: 'Acknowledgement';
    src: url('/fonts/Acknowledgement.otf') format('opentype');
    font-display: swap;
  }
  @font-face {
    font-family: 'Azonix';
    src: url('/fonts/Azonix.otf') format('opentype'),
         url('/fonts/azonix.woff2') format('woff2');
    font-display: swap;
  }

  :root {
    --font-azonix: 'Azonix', 'Impact', sans-serif;
    --font-japanese: 'Japanese3017', 'Noto Serif JP', serif;
    --font-west: 'Acknowledgement', 'Azonix', sans-serif;
    --font-ui: -apple-system, 'Helvetica Neue', sans-serif;
  }

  /* ── ROOT ── */
  .hs-root {
    position: relative;
    width: 100%;
    height: 100vh;
    min-height: 520px;
    max-height: 740px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: #08080c;
  }

  /* ── BACKGROUND ── */
  .hs-bg-wrap {
    position: absolute;
    inset: -24px;        /* oversized so blur edge is hidden */
    z-index: 0;
  }
  .hs-bg-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 20%;
    filter: blur(0px);
    transform: scale(1.04);
    transition: filter 0.8s ease, opacity 0.7s ease;
  }
  /* radial blur: sharp centre, blurry edges */
  .hs-bg-radial {
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 55% 60% at 62% 38%,
      transparent 0%,
      transparent 30%,
      rgba(8,8,12,0.25) 55%,
      rgba(8,8,12,0.75) 80%,
      rgba(8,8,12,0.96) 100%
    );
    backdrop-filter: blur(0px);
    z-index: 1;
  }
  /* additional hard-blurred ring */
  .hs-bg-edge-blur {
    position: absolute;
    inset: 0;
    z-index: 2;
    /* simulate edge blur with layered shadows pointing inward */
    box-shadow:
      inset 80px 0 80px rgba(8,8,12,0.92),
      inset -10px 0 60px rgba(8,8,12,0.2),
      inset 0 80px 80px rgba(8,8,12,0.6),
      inset 0 -0px 0px rgba(8,8,12,0);
  }
  .hs-bg-bottom {
    position: absolute;
    inset: 0;
    z-index: 3;
    background: linear-gradient(
      to top,
      rgba(8,8,12,1) 0%,
      rgba(8,8,12,0.7) 22%,
      rgba(8,8,12,0.2) 45%,
      transparent 65%
    );
  }
  /* accent tint */
  .hs-bg-tint {
    position: absolute;
    inset: 0;
    z-index: 4;
    transition: background 0.9s ease;
  }

  /* ── LABEL (top-left) ── */
  .hs-top-bar {
    position: relative;
    z-index: 10;
    padding: clamp(18px, 3vh, 48px) clamp(20px, 5vw, 60px) 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .hs-label {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 4px 12px;
    border-radius: 100px;
    border: 1px solid;
    font-family: var(--font-azonix);
    font-size: 9px;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    background: rgba(0,0,0,0.35);
    transition: color 0.5s, border-color 0.5s;
    margin-top: 90px;
  }
  .hs-label-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: background 0.5s;
  }
  /* slide counter */
  .hs-counter {
    font-family: var(--font-azonix);
    font-size: 10px;
    letter-spacing: 2px;
    color: rgba(255,255,255,0.28);
  }

  /* ── MAIN CONTENT (bottom-left) ── */
  .hs-body {
    position: relative;
    z-index: 10;
    padding: 0 clamp(20px, 5vw, 60px) clamp(36px, 5vh, 56px);
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 28px;
  }
  .hs-left {
    flex: 1;
    max-width: 520px;
  }

  /* sub-label */
  .hs-sub {
    font-family: var(--font-azonix);
    font-size: 10px;
    letter-spacing: 3px;
    text-transform: uppercase;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: color 0.5s;
  }
  .hs-sub-line {
    display: block;
    height: 1.5px;
    width: 24px;
    border-radius: 1px;
    transition: background 0.5s;
  }

  /* title — max 2 lines, ellipsis */
  .hs-title {
    font-family: var(--font-azonix);
    font-size: clamp(26px, 4.5vw, 54px);
    line-height: 1.06;
    letter-spacing: -0.5px;
    color: #fff;
    margin-bottom: 14px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 480px;
  }
  .hs-title-east { font-family: var(--font-azonix) !important; }
  .hs-title-west { font-family: var(--font-azonix) !important; }

  /* description — 2 lines, ellipsis */
  .hs-desc {
    font-family: var(--font-ui);
    font-size: clamp(13px,1.5vw,16px);
    color: rgba(255,255,255,0.48);
    line-height: 1.75;
    margin-bottom: 28px;
    max-width: 400px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── GLOW BUTTONS ── */
  @keyframes glowUp {
    0%   { box-shadow: 0 0 0 rgba(0,0,0,0); transform: translateY(0); }
    50%  { box-shadow: 0 0 32px var(--btn-glow, rgba(255,255,255,0.3)), 0 8px 24px var(--btn-glow, rgba(255,255,255,0.2)); transform: translateY(-3px); }
    100% { box-shadow: 0 0 20px var(--btn-glow, rgba(255,255,255,0.2)), 0 6px 18px var(--btn-glow, rgba(255,255,255,0.15)); transform: translateY(-2px); }
  }
  @keyframes glowDown {
    0%   { box-shadow: 0 0 0 rgba(0,0,0,0); transform: translateY(0); }
    100% { box-shadow: 0 0 20px var(--btn-glow, rgba(255,255,255,0.2)), 0 6px 18px var(--btn-glow, rgba(255,255,255,0.15)); transform: translateY(-2px); }
  }

  .hs-btns {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
  }

  .hs-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 11px 22px;
    border-radius: 9px;
    font-family: var(--font-azonix);
    font-size: 10px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.25s ease;
    white-space: nowrap;
    border: none;
    outline: none;
    position: relative;
    animation: glowDown 0.4s ease forwards;
  }
  .hs-btn:hover {
    animation: glowUp 0.35s ease forwards;
  }

  /* wireframe accent btn */
  .hs-btn-outline {
    background: rgba(0,0,0,0.3);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid;
    transition: background 0.25s, border-color 0.25s;
  }
  .hs-btn-outline:hover {
    background: rgba(0,0,0,0.5);
  }

  /* filled white btn */
  .hs-btn-white {
    background: #fff;
    color: #08080c !important;
    --btn-glow: rgba(255,255,255,0.5);
    font-weight: 700;
  }
  .hs-btn-white:hover {
    background: rgba(255,255,255,0.92);
  }

  /* ── THUMBNAILS (bottom-right) ── */
  .hs-thumbs {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    flex-shrink: 0;
  }
  .hs-thumb {
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.38s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(255,255,255,0.08);
    flex-shrink: 0;
    width: 64px;
    height: 96px;
  }
  .hs-thumb-active {
    width: 136px !important;
    height: 196px !important;
    border-color: rgba(255,255,255,0.25);
    box-shadow: 0 16px 48px rgba(0,0,0,0.7);
  }
  .hs-thumb img {
    width: 100%; height: 100%;
    object-fit: cover;
    transition: transform 0.55s ease;
    filter: brightness(0.75);
  }
  .hs-thumb-active img { filter: brightness(0.9); }
  .hs-thumb:hover img { transform: scale(1.06); }
  .hs-thumb-label {
    position: absolute;
    bottom: 0; left: 0; right: 0;
    padding: 20px 8px 6px;
    background: linear-gradient(to top, rgba(0,0,0,0.85), transparent);
    font-family: var(--font-azonix);
    font-size: 8px;
    letter-spacing: 2px;
    text-align: center;
    opacity: 0;
    transition: opacity 0.3s;
  }
  .hs-thumb-active .hs-thumb-label { opacity: 1; }

  /* top accent line on active thumb */
  .hs-thumb-active-line {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    border-radius: 1px 1px 0 0;
    transition: background 0.5s;
  }

  /* ── PROGRESS DOTS ── */
  .hs-dots {
    position: absolute;
    bottom: clamp(12px, 2vh, 20px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .hs-dot {
    height: 3px;
    border-radius: 2px;
    background: rgba(255,255,255,0.28);
    cursor: pointer;
    transition: all 0.35s ease;
  }
  .hs-dot-active {
    background: #fff;
  }

  /* ── RESPONSIVE ── */
  @media(max-width: 768px) {
    .hs-thumbs { display: none; }
    .hs-bg-edge-blur { box-shadow: inset 0 60px 60px rgba(8,8,12,0.5), inset 0 -10px 40px rgba(8,8,12,0.1); }
    .hs-bg-radial {
      background: radial-gradient(ellipse 80% 60% at 50% 35%, transparent 0%, transparent 20%, rgba(8,8,12,0.5) 55%, rgba(8,8,12,0.95) 100%);
    }
    .hs-title { font-size: clamp(22px, 8vw, 36px); }
    .hs-body { padding-bottom: 52px; }
  }
  @media(max-width: 480px) {
    .hs-root { max-height: 600px; }
    .hs-title { font-size: clamp(20px, 9vw, 30px); }
    .hs-btns { gap: 8px; }
    .hs-btn { padding: 10px 16px; font-size: 9px; }
  }
`;

// ─── default slides ──────────────────────────────────────────────────────────
const DEFAULT_SLIDES = [
  {
    id: 'd1',
    title: 'ANIMAC',
    description: 'Two worlds. Infinite stories. Anime culture from the East, blockbuster entertainment from the West — curated for the culture.',
    category: 'east',
    image: 'https://i.pinimg.com/1200x/2d/50/93/2d509399aceab6e117c84583a5b1679f.jpg',
    link: '/outrange/east',
    contentType: 'default',
  },
  {
    id: 'd2',
    title: 'ANIMAC',
    description: 'Hollywood blockbusters, Disney animations, and streaming series reviews from around the world.',
    category: 'west',
    image: 'https://i.pinimg.com/1200x/32/a7/dc/32a7dc5ab50029a87761aec1ad3533ae.jpg',
    link: '/outrange/west',
    contentType: 'default',
  },
  {
    id: 'd3',
    title: 'ANIMAC',
    description: 'Your ultimate destination for anime, movies, cartoons, and entertainment culture from around the globe.',
    category: 'neutral',
    image: 'https://i.pinimg.com/1200x/17/cf/72/17cf72663ad6e3d1c23db253b01fe6dd.jpg',
    link: '/outrange',
    contentType: 'default',
  },
];

// ─── HeroSection ─────────────────────────────────────────────────────────────
const HeroSection = ({ featuredContent }) => {
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef(null);

  // Build slides from featuredContent or defaults - with defensive coding
  // Shuffle the featuredContent to show different articles each time
  const slides = React.useMemo(() => {
    try {
      if (featuredContent && Array.isArray(featuredContent) && featuredContent.length > 0) {
        // Create a copy of the array and shuffle it
        const shuffled = [...featuredContent].sort(() => 0.5 - Math.random());
        // Take first 5 items from shuffled array
        return shuffled.slice(0, 5).map((a) => ({
          id: String(a?.id || 'default'),
          title: a?.title || 'ANIMAC',
          description: stripHtml(a?.excerpt) || 'Discover the latest stories curated for the culture.',
          category: a?.category || 'neutral',
          image: a?.featured_image || '/assets/animac-neutral-poster.jpg',
          link: a?.slug ? `/article/${a.slug}` : '/outrange',
          author: a?.author,
          readTime: a?.read_time || 5,
          contentType: a?.contentType || 'article',
          trailerUrl: a?.trailerUrl || null,
          year: a?.year || null,
          rating: a?.rating || null,
          type: a?.type || null,
        }));
      }
    } catch (err) {
      console.error("HeroSection slides error:", err);
    }
    return DEFAULT_SLIDES;
  }, [featuredContent]);

  // Ensure slides is always an array
  const safeSlides = Array.isArray(slides) && slides.length > 0 ? slides : DEFAULT_SLIDES;

  // Auto-advance with error handling
  const advance = React.useCallback((cur) => {
    try {
      setFading(true);
      setTimeout(() => {
        setActive((cur + 1) % safeSlides.length);
        setFading(false);
      }, 380);
    } catch (err) {
      console.error("Advance error:", err);
    }
  }, [safeSlides]);

  useEffect(() => {
    try {
      timerRef.current = setInterval(() => advance(active), 6000);
    } catch (err) {
      console.error("Timer error:", err);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, advance]);

  const switchTo = (i) => {
    if (i === active || fading) return;
    if (timerRef.current) clearInterval(timerRef.current);
    setFading(true);
    setTimeout(() => {
      setActive(i);
      setFading(false);
    }, 340);
  };

  const s = safeSlides[active] || safeSlides[0];
  const accent = accentOf(s.category);
  const glow = glowOf(s.category);
  const label = labelOf(s.category);
  const isEast = s.category === 'east';
  const isWest = s.category === 'west';

  return (
    <>
      <style>{STYLES}</style>
      <div className="hs-root">

        {/* ── BACKGROUND ── */}
        <div className="hs-bg-wrap">
          <AnimatePresence mode="sync">
            <motion.img
              key={active}
              src={s.image}
              alt=""
              className="hs-bg-img"
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1.04 }}
exit={{ opacity: 0 }}
               transition={{ duration: 0.75 }}
            />
          </AnimatePresence>
          <div className="hs-bg-radial" />
          <div className="hs-bg-edge-blur" />
          <div className="hs-bg-bottom" />
          <div
            className="hs-bg-tint"
            style={{ background: `radial-gradient(ellipse 50% 50% at 65% 35%, ${accent}12 0%, transparent 60%)` }}
          />
        </div>

        {/* ── TOP BAR: label + counter ── */}
        <div className="hs-top-bar">
          <motion.div
            key={`label-${active}`}
            initial={{ opacity: 0, y: -8 }}
animate={{ opacity: 1, y: 0 }}
             transition={{ duration: 0.45 }}
             className="hs-label"
             style={{ color: accent, borderColor: `${accent}40` }}
           >
             <span className="hs-label-dot" style={{ background: accent }} />
            {label}
          </motion.div>
          <span className="hs-counter">
            {String(active + 1).padStart(2, '0')}&nbsp;/&nbsp;{String(safeSlides.length).padStart(2, '0')}
          </span>
        </div>

        {/* ── MAIN BODY ── */}
        <div className="hs-body">
          <div className="hs-left">
            {/* sub-label line */}
            <motion.div
              key={`sub-${active}`}
initial={{ opacity: 0, x: -12 }}
               animate={{ opacity: 1, x: 0 }}
               transition={{ duration: 0.5, delay: 0.05 }}
               className="hs-sub"
               style={{ color: accent }}
             >
               <span className="hs-sub-line" style={{ background: accent }} />
              {s.author
                ? `By ${s.author}`
                : isEast
                  ? 'East Portal'
                  : isWest
                    ? 'West Portal'
                    : 'All Content'}
            </motion.div>

            {/* title */}
            <motion.h1
              key={`title-${active}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: fading ? 0 : 1, y: fading ? 8 : 0 }}
              transition={{ duration: 0.45, delay: 0.08 }}
              className={`hs-title${isEast ? ' hs-title-east' : isWest ? ' hs-title-west' : ''}`}>
              {s.title}
            </motion.h1>

            {/* description */}
            <motion.p
              key={`desc-${active}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: fading ? 0 : 1, y: fading ? 6 : 0 }}
              transition={{ duration: 0.45, delay: 0.12 }}
              className="hs-desc"
            >
              {s.description}
            </motion.p>

            {/* buttons */}
            <motion.div
              key={`btns-${active}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.18 }}
              className="hs-btns"
            >
              <Link
                to={s.link || `/article/${s.slug}`}
                className="hs-btn hs-btn-outline"
                style={{
                  color: accent,
                  borderColor: `${accent}55`,
                  '--btn-glow': glow,
                }}
              >
                <ArrowUpRight size={12} />
                Read More
              </Link>

              <Link
                to={s.category === 'east' ? '/outrange/east' : s.category === 'west' ? '/outrange/west' : '/outrange'}
                className="hs-btn hs-btn-white"
              >
                More Info <ChevronRight size={12} />
              </Link>
            </motion.div>
          </div>

          {/* ── THUMBNAILS (desktop only) ── */}
          <div className="hs-thumbs">
            {safeSlides.map((sl, i) => {
              const slAccent = accentOf(sl.category);
              const isActive = i === active;
              return (
                <div
                  key={sl.id}
                  className={`hs-thumb${isActive ? ' hs-thumb-active' : ''}`}
                  onClick={() => switchTo(i)}
                  style={{ borderColor: isActive ? `${slAccent}45` : 'rgba(255,255,255,0.07)' }}
                >
                  {/* top accent line */}
                  <div
                    className="hs-thumb-active-line"
                    style={{ background: isActive ? slAccent : 'transparent' }}
                  />
                  <img src={sl.image} alt={sl.title} loading="lazy" />
                  <div className="hs-thumb-label" style={{ color: slAccent }}>
                    {sl.category === 'east'
                      ? 'EAST'
                      : sl.category === 'west'
                        ? 'WEST'
                        : 'HUB'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── PROGRESS DOTS ── */}
        <div className="hs-dots">
          {safeSlides.map((_, i) => (
            <div
              key={i}
              className={`hs-dot${i === active ? ' hs-dot-active' : ''}`}
              style={{ width: i === active ? '24px' : '6px' }}
              onClick={() => switchTo(i)}
            />
          ))}
        </div>

      </div>
    </>
  );
};

export default HeroSection;